// A contractor's insurance and accreditations (Task U): the overall status, what their trade requires, each record with
// its expiry and state, verify / turn down an upload from the contractor, add one, remove one.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, FileText, Plus, Trash2, X } from 'lucide-react';
import { Badge, Card, Button, Input, Money, SkeletonRows } from '../ui';
import { Can } from '../Can';
import { formatDate } from '../../lib/viz';
import { openStoredFile } from '../../lib/reservations';
import { complianceApi, STATUS_TONE, type ComplianceRecord, type ComplianceSummary } from '../../lib/maintenance';
import { ComplianceForm } from './ComplianceForm';

export function StatusChip({ status, label }: { status: ComplianceSummary['status'] | null | undefined; label?: string }) {
  if (!status) return <span className="text-ink-300">—</span>;
  return <Badge value={label ?? status} tone={STATUS_TONE[status]} />;
}

const KIND_TONE = { OK: 'good', EXPIRING: 'warning', EXPIRED: 'critical', MISSING: 'critical' } as const;
// Labels are proper names (Gas Safe, NICEIC): kept as written, not run through Badge's sentence case.
const PILL = { good: 'border-emerald-200 bg-emerald-50 text-emerald-700', warning: 'border-amber-200 bg-amber-50 text-amber-800', critical: 'border-red-200 bg-red-50 text-red-700' } as const;

export function SummaryStrip({ summary }: { summary: ComplianceSummary }) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2"><StatusChip status={summary.status} label={summary.label} />{summary.pending > 0 && <Badge value={`${summary.pending} waiting to be checked`} tone="neutral" />}</div>
      <ul className="flex flex-wrap gap-1.5">{summary.kinds.map((k) => (
        <li key={k.kind + k.label} className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-[3px] text-[11px] font-medium ${PILL[KIND_TONE[k.state]]}`}>
          <span className="h-1.5 w-1.5 rounded-full bg-current" />{k.label}{k.required ? ' (required)' : ''}: {k.state === 'MISSING' ? 'missing' : k.expiresOn ? formatDate(k.expiresOn) : 'no expiry'}</li>))}</ul>
      {summary.issues.length > 0 && <ul className="list-disc pl-5 text-[12.5px] text-red-700">{summary.issues.map((i) => <li key={i}>{i}</li>)}</ul>}
    </div>
  );
}

export function RecordRow({ r, actions }: { r: ComplianceRecord; actions?: React.ReactNode }) {
  const gone = !!r.expiresOn && r.expiresOn < new Date().toISOString().slice(0, 10);
  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2"><span className="font-medium text-ink-800">{r.label}</span><Badge value={r.state} />{r.state === 'VERIFIED' && gone && <Badge value="EXPIRED" />}</div>
        <div className="text-[12px] text-ink-500">{[r.reference, r.expiresOn ? `runs to ${formatDate(r.expiresOn)}` : 'no expiry', r.submittedByName ? `sent by ${r.submittedByName}` : null, r.verifiedByName ? `checked by ${r.verifiedByName}` : null].filter(Boolean).join(' · ')}
          {r.coverMinor ? <> · cover <Money minor={r.coverMinor} currency={r.currency} /></> : null}</div>
        {r.rejectedReason && <div className="text-[12px] text-red-700">Turned down: {r.rejectedReason}</div>}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-1.5">
        {r.documentUrl && <Button size="sm" variant="secondary" onClick={() => void openStoredFile(r.documentUrl!)}><FileText className="h-3.5 w-3.5" /> Open</Button>}
        {actions}
      </div>
    </li>
  );
}

export function ComplianceCard({ vendorId }: { vendorId: string }) {
  const qc = useQueryClient();
  const key = ['vendor-compliance', vendorId];
  const q = useQuery({ queryKey: key, queryFn: () => complianceApi.forVendor(vendorId) });
  const [adding, setAdding] = useState(false);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const done = () => { setError(null); void qc.invalidateQueries({ queryKey: key }); void qc.invalidateQueries({ queryKey: ['/vendors'] }); };
  const onError = (e: Error) => setError(e.message);
  const add = useMutation({ mutationFn: complianceApi.add.bind(null, vendorId), onSuccess: () => { setAdding(false); done(); }, onError });
  const verify = useMutation({ mutationFn: (id: string) => complianceApi.verify(id), onSuccess: done, onError });
  const reject = useMutation({ mutationFn: () => complianceApi.reject(rejecting!, reason), onSuccess: () => { setRejecting(null); setReason(''); done(); }, onError });
  const remove = useMutation({ mutationFn: (id: string) => complianceApi.remove(id), onSuccess: done, onError });
  const data = q.data;

  return (
    <Card title="Insurance & accreditations" subtitle={data ? `Required for ${data.vendor.trade?.toLowerCase() ?? 'their trade'}: ${data.summary.required.map((k) => data.summary.kinds.find((x) => x.kind === k)?.label ?? k).join(', ')}` : undefined}
      actions={<Can do="vendors.update"><Button size="sm" variant="secondary" onClick={() => setAdding((v) => !v)}><Plus className="h-3.5 w-3.5" /> Add</Button></Can>}>
      {!data ? <SkeletonRows rows={4} cols={3} /> : (
        <div className="space-y-4">
          <SummaryStrip summary={data.summary} />
          {adding && <ComplianceForm onSubmit={(i) => add.mutate(i)} onCancel={() => setAdding(false)} pending={add.isPending} error={error} submitLabel="Add — I have seen it" />}
          {!adding && error && <p className="rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-[12.5px] text-red-700">{error}</p>}
          {!data.records.length ? <p className="text-[13px] text-ink-400">Nothing on file.</p> : (
            <ul className="divide-y divide-ink-100">{data.records.map((r) => (
              <RecordRow key={r.id} r={r} actions={<>
                {r.state === 'PENDING' && <Can do="vendors.verify">
                  <Button size="sm" onClick={() => verify.mutate(r.id)} disabled={verify.isPending}><Check className="h-3.5 w-3.5" /> Verify</Button>
                  <Button size="sm" variant="ghost" onClick={() => setRejecting(r.id)}><X className="h-3.5 w-3.5" /> Turn down</Button>
                </Can>}
                <Can do="vendors.update"><button onClick={() => remove.mutate(r.id)} className="rounded-full p-1.5 text-ink-300 hover:bg-red-50 hover:text-red-500" title="Remove"><Trash2 className="h-3.5 w-3.5" /></button></Can>
              </>} />))}</ul>
          )}
          {rejecting && (
            <div className="flex flex-col gap-2 rounded-xl border border-red-100 bg-red-50/50 p-3 sm:flex-row">
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why — e.g. the schedule is for another firm" className="w-full" />
              <div className="flex gap-2"><Button size="sm" variant="ghost" onClick={() => setRejecting(null)}>Cancel</Button><Button size="sm" variant="danger" disabled={reason.trim().length < 3 || reject.isPending} onClick={() => reject.mutate()}>Turn down</Button></div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
